import { random } from 'lodash';
import { z } from 'zod';
import { EventAttendeeTypeEnum } from '../Interfaces/Calendar/EventAttendeeTypeEnum';
import { EventBodyContentTypeEnum } from '../Interfaces/Calendar/EventBodyContentTypeEnum';
import { ICalendarEvent } from '../Interfaces/Calendar/ICalendarEvent';
import { ICalendarEventAttendee } from '../Interfaces/Calendar/ICalendarEventAttendee';
import { ICalendarEventAttendeeForm } from '../Interfaces/Calendar/ICalendarEventAttendeeForm';
import { ICalendarEventForm } from '../Interfaces/Calendar/ICalendarEventForm';
import { ILegalClient } from '../Interfaces/ILegalClient';

export const issueOptionLabel = ({
  emailAddress: { address, name },
}: ICalendarEventAttendeeForm) => `${name} (${address || 'Keine E-Mail'})`;

export const isOptionDisabled = ({
  emailAddress: { address },
}: ICalendarEventAttendeeForm) => !address;

export const issueAttendees = (
  legalClients: ILegalClient[]
): ICalendarEventAttendeeForm[] =>
  legalClients.map(({ idLegalClient, FirstName, LastName, Email }) =>
    transformLegalClientToAttendee(
      idLegalClient,
      FirstName || '',
      LastName,
      Email || ''
    )
  );

export const createOption = (input: string): ICalendarEventAttendeeForm =>
  transformLegalClientToAttendee(
    -random(1, Number.MAX_SAFE_INTEGER),
    '',
    '',
    input
  );

export const transformEvent = (
  event: ICalendarEvent,
  legalClients: ILegalClient[]
): ICalendarEventForm => {
  const existingMap = new Map(
    legalClients.filter(lc => !lc.Email).map(lc => [lc.Email, lc])
  );

  const onMapLegalClientToAttendee = (
    attendees: ICalendarEventAttendee[]
  ): ICalendarEventAttendeeForm[] =>
    attendees.map(a => {
      const foundLegalClient = existingMap.get(a.emailAddress.address);
      if (
        foundLegalClient &&
        foundLegalClient.LastName === a.emailAddress.name
      ) {
        return {
          ...a,
          id: foundLegalClient.idLegalClient,
        };
      } else {
        return {
          ...a,
          id: -random(1, Number.MAX_SAFE_INTEGER), // Means they are external
        };
      }
    });

  const requiredAttendees = (event.attendees ?? []).filter(
    a => a.type === EventAttendeeTypeEnum.Required
  );
  const optionalAttendees = (event.attendees ?? []).filter(
    a => a.type === EventAttendeeTypeEnum.Optional
  );

  return {
    ...event,
    body: {
      contentType: EventBodyContentTypeEnum.Text,
      content: event.body.content
        ? parseHtmlToText(event.body.content)
        : undefined,
    },
    requiredAttendees: onMapLegalClientToAttendee(requiredAttendees),
    optionalAttendees: onMapLegalClientToAttendee(optionalAttendees),
  };
};

const parseHtmlToText = (html: string) => {
  if (!html) return undefined;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || undefined;
};

const transformLegalClientToAttendee = (
  id: number,
  firstName: string,
  lastName: string,
  email: string
): ICalendarEventAttendeeForm => ({
  id,
  emailAddress: {
    name: !firstName && !lastName ? '' : `${firstName} ${lastName}`,
    address: email,
  },
});

export const isValidEmail = (email: string): boolean =>
  z.string().email().safeParse(email).success;
